/*
 * NBB PDP Rails - client-rendered recommendation rails (shade / sizes / vendor).
 * Each rail lazy-inits via IntersectionObserver (rootMargin 400px) and only then
 * fetches /search/suggest.json. A shared registry (seeded from every
 * .card-wrapper[data-product] already in the DOM - server rails and the
 * "You May Also Like" list included) keeps a product from appearing twice.
 * Rails with fewer than 2 unique results stay hidden.
 */
(function () {
  'use strict';

  var registry = new Set();
  var ymalHandles = new Set();

  function collectDomHandles() {
    document.querySelectorAll('.card-wrapper[data-product]').forEach(function (el) {
      var h = el.getAttribute('data-product');
      if (!h) return;
      registry.add(h);
      if (el.closest('product-recommendations')) ymalHandles.add(h);
    });
  }

  function norm(s) {
    return (s || '').toLowerCase().replace(/\s+/g, ' ').trim();
  }

  function formatMoney(fmt, amount) {
    if (!amount) return '';
    var n = parseFloat(amount);
    if (!isNaN(n)) amount = n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (!fmt) return '$' + amount;
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/g, amount);
  }

  function rangeText(min, max, fmt) {
    if (min && max && parseFloat(min) !== parseFloat(max)) {
      return formatMoney(fmt, min) + ' - ' + formatMoney(fmt, max);
    }
    return formatMoney(fmt, min || max);
  }

  function imgUrl(url, width) {
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function div(cls) {
    var el = document.createElement('div');
    el.className = cls;
    return el;
  }

  /* --- query builders ------------------------------------------------ */

  // Prefer a trailing shade code ("GC S86" -> "S86"); otherwise the segment
  // after the last " - " with numeric codes removed ("061 Wineberry" -> "Wineberry").
  var SHADE_TYPE_RE = /polish|gel|dip|lacquer|powder|colou?r|shade/i;
  var PACK_RE = /\d+\s*\/|\b(?:oz|ml|box|boxes|case|cases|pallet|gallon|bucket|jar|tube|refill|single|pack|pcs?|ct)\b/i;

  function shadeQuery(title, type) {
    if (!SHADE_TYPE_RE.test(type || '')) return null; // chairs, gloves, tools have no shade
    var m = title.match(/\b[A-Z]{1,3} ?\d{2,4}[A-Z]?\b(?=[^\d]*$)/);
    if (m) return m[0];
    var i = title.lastIndexOf(' - ');
    if (i === -1) return null;
    var seg = title
      .slice(i + 3)
      .replace(/\b[A-Z]{1,3} ?\d{2,4}[A-Z]?\b/g, ' ')
      .replace(/(^|\s)#?\d+[A-Z]?(?=\s|$)/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!seg || PACK_RE.test(seg)) return null;
    return seg;
  }

  // Strip pack/size suffix tokens off the title until nothing more strips.
  function sizePrefix(title) {
    var t = title.trim();
    var tokenRe = /(?:\(\s*[^)]*\)|\d+(?:\.\d+)?\s*(?:oz|ml|g|kg|lb|lbs|ct|pc|pcs)\.?|\d+\s*\/\s*[A-Za-z]+|gallon|bucket|case|box|boxes|single|pallet|jar|tube|refill|liter|litre|per|mix\s*#?\d*|#\d+|[-–—|,&])\s*$/i;
    var prev;
    do {
      prev = t;
      t = t.replace(tokenRe, '').trim();
    } while (t !== prev);
    if (t.split(/\s+/).length < 2) return null;
    return t;
  }

  // "OPI Nail Polish Display Stand" must not count as a size of "... Display Case":
  // the part after the shared prefix has to strip down to nothing.
  function isPackRemainder(remainder) {
    var r = remainder.trim();
    if (!r) return false;
    var tokenRe = /^(?:\(\s*[^)]*\)|\d+(?:\.\d+)?\s*(?:oz|ml|g|kg|lb|lbs|ct|pc|pcs)\.?|\d+\s*\/\s*[A-Za-z]+|gallon|bucket|case|box|boxes|single|pallet|jar|tube|refill|liter|litre|per|mix\s*#?\d*|#\d+|\d+|[-–—|,&])\s*/i;
    var prev;
    do { prev = r; r = r.replace(tokenRe, '').trim(); } while (r && r !== prev);
    return r === '';
  }

  /* --- card DOM (mirrors snippets/product-card.liquid classes) -------- */

  function buildPrice(p, fmt) {
    var onSale = parseFloat(p.compare_at_price_min || 0) > parseFloat(p.price_min || p.price || 0);
    var price = div('price' + (onSale ? ' price--on-sale' : ''));
    var dl = document.createElement('dl');
    var row = div(onSale ? 'price__sale' : 'price__regular');
    var dd = document.createElement('dd');
    var item = document.createElement('span');
    item.className = onSale ? 'price-item price-item--sale' : 'price-item price-item--regular';
    item.textContent = rangeText(p.price_min || p.price, p.price_max, fmt);
    dd.appendChild(item);
    row.appendChild(dd);
    if (onSale) {
      var cdd = document.createElement('dd');
      cdd.className = 'price__compare';
      var s = document.createElement('s');
      s.className = 'price-item price-item--regular';
      s.textContent = rangeText(p.compare_at_price_min, p.compare_at_price_max, fmt);
      cdd.appendChild(s);
      row.appendChild(cdd);
    }
    dl.appendChild(row);
    price.appendChild(dl);
    return price;
  }

  function buildCard(p, scheme, fmt) {
    var wrap = div('card-wrapper color-' + scheme);
    wrap.setAttribute('data-product', p.handle);

    var card = div('card card--product');
    var inner = div('card__inner full-unstyled-link');
    var media = div('media media--transparent media--hover-effect');
    media.style.paddingBottom = '100%';
    if (p.featured_image && p.featured_image.url) {
      var img = document.createElement('img');
      img.loading = 'lazy';
      img.className = 'motion-reduce media--first';
      var ar = p.featured_image.aspect_ratio || 1;
      img.width = 360;
      img.height = Math.round(360 / ar);
      img.src = imgUrl(p.featured_image.url, 360);
      img.srcset = imgUrl(p.featured_image.url, 360) + ' 360w, ' + imgUrl(p.featured_image.url, 720) + ' 720w';
      img.sizes = '(min-width: 990px) 220px, 42vw';
      img.alt = p.featured_image.alt || p.title;
      media.appendChild(img);
    }
    inner.appendChild(media);
    card.appendChild(inner);
    wrap.appendChild(card);

    var info = div('card-information');
    var infoWrap = div('card-information__wrapper');
    var group = div('card-information__group');
    var left = div('card-information__group-left');
    var h3 = document.createElement('h3');
    h3.className = 'card__title h5';
    var a = document.createElement('a');
    a.className = 'full-unstyled-link';
    a.href = p.url;
    a.textContent = p.title;
    h3.appendChild(a);
    left.appendChild(h3);
    group.appendChild(left);
    infoWrap.appendChild(group);
    infoWrap.appendChild(buildPrice(p, fmt));
    info.appendChild(infoWrap);
    wrap.appendChild(info);

    var overlay = document.createElement('a');
    overlay.href = p.url;
    overlay.className = 'link link--overlay card-wrapper__link--overlay';
    overlay.setAttribute('aria-label', p.title);
    wrap.appendChild(overlay);
    return wrap;
  }

  /* --- rail init ------------------------------------------------------ */

  function suggest(query, fields) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    var url =
      root +
      'search/suggest.json?q=' +
      encodeURIComponent(query) +
      '&resources[type]=product&resources[limit]=10' +
      '&resources[options][unavailable_products]=hide&resources[options][fields]=' +
      fields;
    return fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('suggest ' + r.status);
        return r.json();
      })
      .then(function (d) {
        return (d.resources && d.resources.results && d.resources.results.products) || [];
      });
  }

  function initRail(rail, ctx) {
    var type = rail.getAttribute('data-rail');
    var max = Math.min(parseInt(rail.getAttribute('data-max'), 10) || 8, 10);
    var query = null;
    var fields = 'title';
    var accept = function () { return true; };
    // shade: overlap with "You May Also Like" is fine and one match is still worth showing
    var ignoreYmal = type === 'shade';
    var minItems = type === 'shade' ? 1 : 2;

    if (type === 'shade') {
      query = shadeQuery(ctx.title, ctx.type);
    } else if (type === 'sizes') {
      var prefix = sizePrefix(ctx.title);
      if (prefix) {
        query = '"' + prefix + '"';
        var nPrefix = norm(prefix);
        accept = function (p) {
          var nt = norm(p.title);
          return nt.indexOf(nPrefix) === 0 && isPackRemainder(nt.slice(nPrefix.length));
        };
      }
    } else if (type === 'vendor') {
      query = ctx.vendor;
      fields = 'vendor';
      var nVendor = norm(ctx.vendor);
      accept = function (p) { return norm(p.vendor) === nVendor; };
    }
    if (!query) return;

    suggest(query, fields)
      .then(function (products) {
        collectDomHandles(); // server rails + "You May Also Like" register first
        var items = [];
        for (var i = 0; i < products.length && items.length < max; i++) {
          var p = products[i];
          if (!p.handle || p.available === false) continue;
          if (p.handle === ctx.handle) continue;
          if (registry.has(p.handle) && !(ignoreYmal && ymalHandles.has(p.handle))) continue;
          if (!accept(p)) continue;
          registry.add(p.handle);
          items.push(p);
        }
        if (items.length < minItems) {
          items.forEach(function (p) { registry.delete(p.handle); });
          return;
        }
        collectDomHandles(); // YMAL may have populated during our fetch
        var row = rail.querySelector('.nbb-rails__row');
        items.forEach(function (p) {
          var item = div('nbb-rails__item');
          item.appendChild(buildCard(p, ctx.scheme, ctx.moneyFormat));
          row.appendChild(item);
        });
        rail.removeAttribute('hidden');
        rendered.push(rail);
      })
      .catch(function () { /* leave the rail hidden */ });
  }

  var rendered = []; // client rails currently showing cards

  // "You May Also Like" fills itself from its own fetch; if it lands after a
  // client rail rendered, drop the rail's now-duplicate cards (hide if < 2 left).
  function watchYmal() {
    var ymal = document.querySelector('product-recommendations');
    if (!ymal || !('MutationObserver' in window)) return;
    var mo = new MutationObserver(function () {
      var theirs = new Set();
      ymal.querySelectorAll('.card-wrapper[data-product]').forEach(function (el) {
        theirs.add(el.getAttribute('data-product'));
      });
      if (!theirs.size) return;
      mo.disconnect();
      rendered.forEach(function (rail) {
        if (rail.getAttribute('data-rail') === 'shade') return;
        rail.querySelectorAll('.nbb-rails__item').forEach(function (item) {
          var card = item.querySelector('.card-wrapper[data-product]');
          if (card && theirs.has(card.getAttribute('data-product'))) item.remove();
        });
        if (rail.querySelectorAll('.nbb-rails__item').length < 2) rail.setAttribute('hidden', '');
      });
    });
    mo.observe(ymal, { childList: true, subtree: true });
  }

  function initRoot(root) {
    if (root.dataset.nbbRailsInit) return;
    root.dataset.nbbRailsInit = '1';
    watchYmal();

    var ctx = {
      handle: root.getAttribute('data-product-handle') || '',
      title: root.getAttribute('data-product-title') || '',
      vendor: root.getAttribute('data-product-vendor') || '',
      type: root.getAttribute('data-product-type') || '',
      scheme: root.getAttribute('data-card-scheme') || 'background-1',
      moneyFormat: root.getAttribute('data-money-format') || ''
    };
    if (ctx.handle) registry.add(ctx.handle);

    var rails = root.querySelectorAll('[data-rail]');
    if (!rails.length) return;

    var initAll = function () {
      rails.forEach(function (rail) { initRail(rail, ctx); });
    };
    if (!('IntersectionObserver' in window)) {
      initAll();
      return;
    }
    // Observe the (always-rendered) root, not the display:none rails -
    // hidden elements have no layout box and would never intersect.
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.disconnect();
          initAll();
        });
      },
      { rootMargin: '400px 0px' }
    );
    io.observe(root);
  }

  function boot() {
    document.querySelectorAll('[data-nbb-rails]').forEach(initRoot);
  }

  boot();
  document.addEventListener('shopify:section:load', boot);
})();
